import _ from "lodash";

/**
 * ICH's Portfolio (jsreport ich-selections-portfolio-2) prints one activity's selections. A home's
 * activities share one contract: a stage run on another activity (plumbing, appliances, lighting…)
 * prices into an addendum drafted from the home's contract, so its job also carries the home's
 * structural lines, and the home gets the stage's lines back when the stage is saved. The template
 * prints every line that matches none of the activity's lists under "Additional Selections", so a
 * plumbing portfolio would otherwise list the whole structural contract there.
 *
 * A line belongs to the activity the player tagged it for (projectOpsContribution.activityId). An
 * untagged line belongs to the activity that owns the contract (the home's anchor), except in another
 * activity's session, where a line its earlier contract documents do not have is the stage's own
 * (Cloud's Price List does not tag the lines it adds).
 */
export const ICH_PORTFOLIO_REPORT_ID = "ich-selections-portfolio-2";

export const isIchPortfolioReport = (templateId) => templateId === ICH_PORTFOLIO_REPORT_ID;

const text = (value) => (value === undefined || value === null ? "" : String(value).trim());

// As the player's contractLineKey: a contract line keeps its lineId (else id) across documents.
const lineKey = (line) => text(line.lineId) || text(line.id);

function getContractActivityId(activity) {
	return text(_.get(activity, ["projectContractAnchor", "activityId"]))
		|| text(_.get(activity, ["projectOps", "projectContractAnchor", "activityId"]))
		|| text(activity.id);
}

// The lines the session's contract had before its current document: the ones it inherited.
function getInheritedLineKeys(activity) {
	const contract = _.isPlainObject(activity.pricingContractProto) ? activity.pricingContractProto : {};
	const activeDocumentId = text(contract.activeDocumentId);
	const keys = new Set();
	(Array.isArray(contract.documents) ? contract.documents : []).forEach((document) => {
		if (!_.isPlainObject(document) || text(document.id) === activeDocumentId) {
			return;
		}
		(Array.isArray(document.invoiceItems) ? document.invoiceItems : []).forEach((line) => {
			const key = _.isPlainObject(line) ? lineKey(line) : "";
			if (key) {
				keys.add(key);
			}
		});
	});
	return keys;
}

/**
 * @param {Object} activity A saved job (or one with the Price List's draft lines applied).
 * @returns {Object[]} The job's invoice lines that belong to its own activity.
 */
export function getActivityPortfolioLines(activity) {
	const lines = activity && Array.isArray(activity.invoiceItems) ? activity.invoiceItems : [];
	const activityId = text(activity && activity.id);
	if (!activityId) {
		return lines;
	}
	const inheritedKeys = getContractActivityId(activity) === activityId ? null : getInheritedLineKeys(activity);
	return lines.filter((line) => {
		if (!_.isPlainObject(line)) {
			return false;
		}
		const taggedActivityId = text(_.get(line, ["projectOpsContribution", "activityId"]));
		if (taggedActivityId) {
			return taggedActivityId === activityId;
		}
		if (!inheritedKeys) {
			return true;
		}
		const key = lineKey(line);
		return !key || !inheritedKeys.has(key);
	});
}

// The Portfolio's data: the job with only its own activity's lines.
export function prepareIchPortfolioReportData(activity) {
	if (!activity || !Array.isArray(activity.invoiceItems)) {
		return activity;
	}
	return { ...activity, invoiceItems: getActivityPortfolioLines(activity) };
}
